import { useEffect, useRef } from 'react';
import useMarketStructureAnalysisStore from '../Stores/marketStructureAnalysisStore';

import { PREMIUM_SELLERS_WS_URL } from './websocketUrls';
import {
  buildMarketStructureAuthPayload,
  getAuthMode,
} from './buildAuthPayload';

function normalizeMessage(rawMessage) {
  try {
    return JSON.parse(rawMessage);
  } catch (error) {
    console.warn('[MARKET STRUCTURE AI] Failed to parse WS message:', rawMessage);
    return null;
  }
}

function getAnalysisPayload(message) {
  if (!message) return null;

  if (message.type === 'MARKET_STRUCTURE_ANALYSIS') {
    return message.payload || message.analysis || null;
  }

  return null;
}

export default function MarketStructureAnalysisClient() {
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const manuallyClosedRef = useRef(false);
  const authFailedRef = useRef(false);

  const setLoading = useMarketStructureAnalysisStore(state => state.setLoading);
  const setAnalysis = useMarketStructureAnalysisStore(state => state.setAnalysis);
  const setError = useMarketStructureAnalysisStore(state => state.setError);

  useEffect(() => {
    manuallyClosedRef.current = false;
    authFailedRef.current = false;

    async function sendAuth(ws) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      try {
        const authPayload = await buildMarketStructureAuthPayload();

        console.log('[MARKET STRUCTURE AI] Sending AUTH payload:', {
          mode: getAuthMode(),
          type: authPayload.type,
          memberId: authPayload.memberId,
          planId: authPayload.planId,
          status: authPayload.status,
        });

        ws.send(JSON.stringify(authPayload));
      } catch (error) {
        console.error(
          '[MARKET STRUCTURE AI] Failed to build auth payload:',
          error
        );

        authFailedRef.current = true;
        manuallyClosedRef.current = true;

        setError(
          error.message ||
            'Unable to authenticate market structure analysis connection.'
        );

        ws.close();
      }
    }

    function requestAnalysis(ws) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      // console.log('[MARKET STRUCTURE AI] Requesting analysis');

      ws.send(
        JSON.stringify({
          type: 'REQUEST_MARKET_STRUCTURE_ANALYSIS',
        })
      );
    }

    function connect() {
      if (manuallyClosedRef.current || authFailedRef.current) {
        return;
      }

      setLoading(true);


      const ws = new WebSocket(PREMIUM_SELLERS_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        // console.log('[MARKET STRUCTURE AI] Connected');

        reconnectAttemptsRef.current = 0;

        // Backend requires auth before accepting dashboard requests.
        sendAuth(ws);
      };

      ws.onmessage = event => {
        const message = normalizeMessage(event.data);
        if (!message) return;

        console.log('[MARKET STRUCTURE AI] Message received:', message);

        if (message.type === 'AUTH_OK') {
          console.log('[MARKET STRUCTURE AI] Auth OK');
          requestAnalysis(ws);
          return;
        }

        if (message.type === 'AUTH_ERROR') {
          const errorMessage =
            message.message ||
            message.payload?.error ||
            'Market structure authentication failed.';

          console.warn('[MARKET STRUCTURE AI] Auth error:', errorMessage);

          authFailedRef.current = true;
          manuallyClosedRef.current = true;

          setError(errorMessage);

          ws.close();
          return;
        }

        if (message.type === 'ERROR') {
          const errorMessage =
            message.message ||
            message.payload?.error ||
            'Market structure request failed.';

          console.warn('[MARKET STRUCTURE AI] Server error:', errorMessage);
          setError(errorMessage);
          return;
        }

        if (message.type === 'MARKET_STRUCTURE_ANALYSIS_ERROR') {
          const errorMessage =
            message.payload?.error ||
            message.error ||
            'Unable to generate market structure analysis.';

          console.warn('[MARKET STRUCTURE AI] Analysis error:', errorMessage);
          setError(errorMessage);
          setLoading(false);
          return;
        }

        const payload = getAnalysisPayload(message);

        if (payload) {
          // console.log('[MARKET STRUCTURE AI] Received analysis:', payload);
          setAnalysis(payload);
          setLoading(false);
          return;
        }

        // console.log('[MARKET STRUCTURE AI] Unhandled message:', message);
      };

      ws.onerror = error => {
        console.error('[MARKET STRUCTURE AI] WebSocket error:', error);
        setError('Market structure analysis connection error.');
      };

      ws.onclose = event => {
        console.warn('[MARKET STRUCTURE AI] WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });

        wsRef.current = null;

        if (manuallyClosedRef.current || authFailedRef.current) {
          return;
        }

        reconnectAttemptsRef.current += 1;

        const reconnectDelay = Math.min(
          1000 * reconnectAttemptsRef.current,
          10000
        );

        console.log(
          `[MARKET STRUCTURE AI] Reconnecting in ${reconnectDelay}ms... attempt=${reconnectAttemptsRef.current}`
        );

        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay);
      };
    }

    connect();

    return () => {
      manuallyClosedRef.current = true;

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }

      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [setLoading, setAnalysis, setError]);

  return null;
}